import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { CancelAppointmentIcon } from '@/components/atoms/icons';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React from 'react';
import { Modal, StyleSheet, View } from 'react-native';

export interface CancelAppointmentDialogProps {
    visible: boolean;
    /** Title text; defaults to "Cancel Appointment" */
    title?: string;
    /** Description text */
    message?: string;
    /** "No" button text */
    noText?: string;
    /** "Yes" button text */
    yesText?: string;
    onNo: () => void;
    onYes: () => void;
}

export const CancelAppointmentDialog: React.FC<CancelAppointmentDialogProps> = ({
    visible,
    title = 'Cancel Appointment',
    message = 'Are you sure you want to cancel your appointment?',
    noText = 'No',
    yesText = 'Yes',
    onNo,
    onYes,
}) => {
    const colors = useUIColors();

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onNo}
        >
            <View style={styles.overlay}>
                <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                    {/* Icon */}
                    <View style={styles.iconContainer}>
                        <CancelAppointmentIcon size={48} color={colors.fontPrimary} />
                    </View>

                    {/* Title */}
                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.title}
                    >
                        {title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body"
                        color="secondary"
                        style={styles.message}
                    >
                        {message}
                    </Typography>

                    {/* Buttons */}
                    <View style={styles.buttonRow}>
                        <Button
                            title={noText}
                            variant="outline"
                            onPress={() => {
                                trackCatalogEvent({
                                    screen: ALMETLAA_SCREEN.APPOINTMENT,
                                    actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_DIALOG_NO,
                                    eventType: 'action',
                                });
                                onNo();
                            }}
                            style={styles.button}
                            testID="cancel-appointment-no-button"
                        />
                        <Button
                            title={yesText}
                            variant="primary"
                            onPress={() => {
                                trackCatalogEvent({
                                    screen: ALMETLAA_SCREEN.APPOINTMENT,
                                    actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_DIALOG_CONFIRM,
                                    eventType: 'action',
                                });
                                onYes();
                            }}
                            style={styles.button}
                            testID="cancel-appointment-yes-button"
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    container: {
        borderRadius: 16,
        paddingTop: 32,
        paddingBottom: 24,
        paddingHorizontal: 20,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        gap: 16,
    },
    iconContainer: {
        marginBottom: 4,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    message: {
        textAlign: 'center',
        fontSize: 14,
        lineHeight: 21,
        width: '100%',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: 16,
        width: '100%',
        marginTop: 4,
    },
    button: {
        flex: 1,
        height: 48,
    },
});

export default CancelAppointmentDialog;
